/**
 * useDashboardData — powers the rich network-tile landing grid (LANDING).
 *
 * PUSH-DRIVEN, not polled. It:
 * - loads `GET /networks/dashboard` once on mount,
 * - subscribes to the coarse `ROOM_ACTIVITY_CHANGED` beacon on the TENANT context
 *   channel (`useContextChannel`) — so EVERY member watching flips the tile live
 *   (collaborative), not just the run owner. On a ping it optimistically flips the
 *   network's active state and schedules a debounced refetch for true counts, and
 * - refetches on tab-focus (`visibilitychange` → visible) as the self-healing
 *   fallback for a missed/dropped beacon. No steady interval polling.
 *
 * Returns { networks, byId, loading, error, refresh, livePing, live }.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { getNetworksDashboard } from '../services/networksDashboardApi';
import { useContextChannel } from './useContextChannel';

const REFETCH_DEBOUNCE_MS = 1500;

export function useDashboardData(tenantId) {
  const [networks, setNetworks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [livePing, setLivePing] = useState({}); // networkId -> 'RUNNING' | 'IDLE'

  const aliveRef = useRef(true);
  const inFlightRef = useRef(false);
  const debounceRef = useRef(null);

  const refresh = useCallback(async (silent = false) => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    if (!silent) setLoading(true);
    try {
      const { networks: rows } = await getNetworksDashboard();
      if (!aliveRef.current) return;
      setNetworks(rows);
      setError(null);
    } catch (e) {
      if (aliveRef.current) setError(e?.message || 'Failed to load dashboard');
    } finally {
      inFlightRef.current = false;
      if (aliveRef.current && !silent) setLoading(false);
    }
  }, []);

  // Initial load.
  useEffect(() => {
    aliveRef.current = true;
    refresh(false);
    return () => {
      aliveRef.current = false;
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [refresh]);

  // Tab-focus refetch — the fallback that heals a missed beacon without polling.
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const onVis = () => {
      if (document.visibilityState === 'visible') refresh(true);
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [refresh]);

  // Coarse live beacon on the tenant context channel → optimistic flip + refetch.
  const onRoomActivity = useCallback((a) => {
    const nid = a && typeof a.networkId === 'string' ? a.networkId : '';
    if (!nid) return;
    const state = a.state === 'RUNNING' ? 'RUNNING' : 'IDLE';
    // Optimistic: nudge/clear activeRoomCount + stamp the ping so the tile's live
    // dot reacts within ~1s, before the debounced refetch confirms counts.
    setLivePing((m) => ({ ...m, [nid]: state }));
    setNetworks((rows) => rows.map((r) => {
      if (r.networkId !== nid) return r;
      const cur = Number(r.activeRoomCount || 0);
      const next = state === 'RUNNING' ? Math.max(cur, 1) : Math.max(0, cur - 1);
      return { ...r, activeRoomCount: next };
    }));
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => refresh(true), REFETCH_DEBOUNCE_MS);
  }, [refresh]);

  // Context channel: we don't consume CONTEXT_SYNC_STATUS here, only room activity.
  const { connected } = useContextChannel(tenantId, () => {}, onRoomActivity);

  const byId = {};
  for (const n of networks) if (n && n.networkId) byId[n.networkId] = n;

  return { networks, byId, loading, error, refresh, livePing, live: connected };
}
